import { XMarkIcon, CheckIcon } from "@heroicons/react/24/outline"
import React, { useState } from "react"

import Typography from "../../components/shared/Typography"

import { getAddableSectionLayouts } from "./utils/addableSectionLayouts"

// Import section thumbnails
const sectionThumbnails = {
  gallery: require("../../../assets/images/builder/v2/section-thumbnails/gallery-section.png"),
  services: require("../../../assets/images/builder/v2/section-thumbnails/services-section.png"),
  featured_services: require("../../../assets/images/builder/v2/section-thumbnails/services-section.png"),
  about: require("../../../assets/images/builder/v2/section-thumbnails/content-section.png"),
  content: require("../../../assets/images/builder/v2/section-thumbnails/content-section.png"),
  reviews: require("../../../assets/images/builder/v2/section-thumbnails/reviews-section.png"),
  quote: require("../../../assets/images/builder/v2/section-thumbnails/quote-section.png"),
  cta_content: require("../../../assets/images/builder/v2/section-thumbnails/content-section.png"),
  cta_banner: require("../../../assets/images/builder/v2/section-thumbnails/banner-section.png"),
  faq: require("../../../assets/images/builder/v2/section-thumbnails/faq-section.png"),
  keywords: require("../../../assets/images/builder/v2/section-thumbnails/keywords-section.png"),
  credentials: require("../../../assets/images/builder/v2/section-thumbnails/credentials-section.png"),
  sub_hero: require("../../../assets/images/builder/v2/section-thumbnails/content-section.png"),
  contact: require("../../../assets/images/builder/v2/section-thumbnails/content-section.png")
}

const SectionTypeCard = ({ sectionType, isSelected, onSelect }) => {
  // Get section thumbnail directly from SVG imports
  const getSectionThumbnail = (sectionType) => sectionThumbnails[sectionType.value] || sectionThumbnails["content"]

  const sectionConfig = {
    sub_hero: {
      title: "Sub Hero",
      description: "Add a hero section for non-home pages with image and CTA."
    },
    services: {
      title: "Services & Events",
      description: "Showcase some or all of your offerings with a selection of images and details."
    },
    gallery: {
      title: "Gallery",
      description:
        "Showcase your work, environment, or results with a selection of high-quality images that tell your story."
    },
    reviews: {
      title: "Reviews",
      description: "Highlight client testimonials that build trust and speak to the quality of your work."
    },
    about: {
      title: "About",
      description: "Introduce yourself or your practice with an image, story, and call-to-action."
    },
    content: {
      title: "Content",
      description:
        "Share detailed information, stories, or insights that help clients understand your approach and offerings."
    },
    quote: {
      title: "Quote",
      description: "Feature a meaningful quote or message that reflects your values, philosophy, or client experience."
    },
    cta_content: {
      title: "Content",
      description:
        "Share detailed information, stories, or insights that help clients understand your approach and offerings."
    },
    cta_banner: {
      title: "Banner",
      description:
        "Create a bold visual statement with a featured image and message that introduces or emphasizes your brand."
    },
    credentials: {
      title: "Credentials",
      description:
        "Showcase your certifications, training, and qualifications to reinforce your professionalism and expertise."
    },
    faq: {
      title: "FAQ",
      description: "Address common questions to help clients feel informed, comfortable, and confident before booking."
    },
    keywords: {
      title: "Keywords",
      description:
        "Highlight important keywords that help clients quickly understand your specialties and improve search visibility."
    },
    contact: {
      title: "Contact",
      description: "Show your contact form plus phone, email, address, hours, map, and social links."
    }
  }

  const config = sectionConfig[sectionType.value] || {
    title: sectionType.label,
    description:
      "Showcase your work, environment, or results with a selection of high-quality images that tell your story."
  }

  return (
    <div
      className={`group border-b border-gray-200 p-6 transition-all ${
        isSelected ? "border-l-4 border-l-teal bg-teal/5 hover:bg-teal/10" : "hover:bg-gray-50"
      }`}>
      <div className="flex items-center justify-between">
        <div className="flex items-start gap-4">
          {/* Section type icon */}
          <div className="size-12 shrink-0">
            <img
              src={getSectionThumbnail(sectionType)}
              alt={`${config.title} section thumbnail`}
              className="size-full object-contain"
            />
          </div>
          <div className="flex-1">
            <Typography variant="h5" className="mb-1 font-semibold">
              {config.title}
            </Typography>
            <Typography variant="body" className="text-gray-600">
              {config.description}
            </Typography>
          </div>
        </div>
        <div className="ml-4">
          {isSelected ? (
            <button
              className="flex items-center text-teal transition-colors hover:text-teal-dark"
              onClick={(e) => {
                e.preventDefault()
                onSelect(sectionType)
              }}
              title="Click to deselect">
              <CheckIcon className="mr-1 size-4 text-green-500" />
              <span className="text-sm font-medium">Selected</span>
            </button>
          ) : (
            <button
              className="rounded-md bg-teal px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-teal/90"
              onClick={(e) => {
                e.preventDefault()
                onSelect(sectionType)
              }}>
              Select
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

const AddSectionScreen = ({ availableLayouts, onClose, onAddSections, currentPage, existingLayouts = [] }) => {
  const [selectedSections, setSelectedSections] = useState([])

  const handleSelectSection = (sectionType) => {
    setSelectedSections((prev) => {
      const isAlreadySelected = prev.some((s) => s.value === sectionType.value)
      if (isAlreadySelected) {
        return prev.filter((s) => s.value !== sectionType.value)
      } else {
        return [...prev, sectionType]
      }
    })
  }

  const handleClearAll = () => {
    setSelectedSections([])
  }

  const handleAdd = () => {
    if (selectedSections.length > 0) {
      onAddSections(selectedSections)
      onClose()
    }
  }

  const isSelected = (sectionType) => selectedSections.some((s) => s.value === sectionType.value)

  const addableSections = getAddableSectionLayouts({
    availableLayouts,
    currentPage,
    existingLayouts
  })

  return (
    <div className="flex h-full flex-col bg-white">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-gray-200 p-6">
        <Typography variant="h4" className="text-xl font-semibold">
          Add section
        </Typography>
        <button onClick={onClose} className="rounded p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-600">
          <XMarkIcon className="size-6" />
        </button>
      </div>

      {/* Selection Counter */}
      {selectedSections.length > 0 && (
        <div className="flex items-center justify-between border-b border-gray-200 bg-gray-50 px-6 py-3">
          <Typography variant="small" className="text-gray-600">
            {selectedSections.length} section{selectedSections.length !== 1 ? "s" : ""} selected
          </Typography>
          <button
            onClick={handleClearAll}
            className="text-sm text-teal underline transition-colors hover:text-teal-dark">
            Clear all
          </button>
        </div>
      )}

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        {addableSections.map((sectionType) => (
          <SectionTypeCard
            key={sectionType.value}
            sectionType={sectionType}
            isSelected={isSelected(sectionType)}
            onSelect={handleSelectSection}
          />
        ))}
      </div>

      {/* Footer */}
      <div className="flex items-center justify-end space-x-3 border-t border-gray-light bg-white px-8 py-6">
        <button
          className="rounded-md border border-gray-300 bg-white px-6 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
          onClick={onClose}>
          Close
        </button>
        <button
          className="rounded-md bg-teal px-6 py-2 text-sm font-medium text-white transition-colors hover:bg-teal/90 disabled:cursor-not-allowed disabled:opacity-50"
          onClick={handleAdd}
          disabled={selectedSections.length === 0}>
          Add {selectedSections.length > 0 ? `(${selectedSections.length})` : ""}
        </button>
      </div>
    </div>
  )
}

export default AddSectionScreen
